import React from 'react';
import ReactDOMServer from 'react-dom/server';
import {connect} from 'react-redux';

import {LightUserThemeProvider} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {batch} from '../../../helpers/reduxHelpers';
import {makeMapDispatchToProps} from '../../../helpers/reselectHelpers';
import {ParticipantTypesEnum} from '../../../models/api/participantModel';
import {RecipientRolesEnum} from '../../../models/api/recipientModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ThunkAction} from '../../../store/appThunk';
import {
  deleteCalendarEventVersionAction,
  RecurringEventSaveTypesEnum,
} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {composeMessageAction} from '../../../store/data/messages/draftMessagesActions';
import {getTimeFormat} from '../../../store/data/preferences/preferencesSelectors';
import {clearDeletedEventParametersAction} from '../../../store/panels/calendars/calendarsPanelActions';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {CalendarEventEmail} from '../calendarEventEmail';
import {CalendarEventViewerHeaderButtons} from './calendarEventViewerHeaderButtons';

/*
 * Props.
 */

interface CalendarEventViewerHeaderButtonsContainerProps {
  panelId: CalendarsPanelId;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
}

/*
 * Redux.
 */

const mapDispatchToProps = makeMapDispatchToProps<CalendarEventViewerHeaderButtonsContainerProps>()(
  (props) => {
    const {panelId, calendarEventData, visualParticipants} = props;
    return {
      onDeleteClick:
        (
          recurringSaveType: RecurringEventSaveTypesEnum | undefined,
          shouldNotifyGuests?: boolean,
        ): ThunkAction =>
        (dispatch) => {
          batch(() => {
            dispatch(
              deleteCalendarEventVersionAction({
                calendarEventData,
                visualParticipants,
                recurringSaveType,
                shouldNotifyGuests,
              }),
            );
            dispatch(clearDeletedEventParametersAction(panelId, calendarEventData));
          });
        },
      onEmailParticipantsClick: (): ThunkAction => (dispatch, getState) => {
        if (!calendarEventData.channel) {
          return;
        }

        const timeFormat = getTimeFormat(getState());
        const calendarEventEmailHtml = ReactDOMServer.renderToStaticMarkup(
          <LightUserThemeProvider>
            <CalendarEventEmail
              channel={calendarEventData.channel}
              calendarEvent={calendarEventData.calendarEvent}
              calendarEventVersion={calendarEventData.calendarEventVersion}
              visualParticipants={visualParticipants}
              timeFormat={timeFormat}
            />
          </LightUserThemeProvider>,
        );

        const recipients = props.visualParticipants
          .filter((p) => p.type !== ParticipantTypesEnum.RESOURCE)
          .map((p) => ({
            ...p,
            role: RecipientRolesEnum.TO,
          }));

        dispatch(
          composeMessageAction({
            channelId: calendarEventData.channel.id,
            subject: calendarEventData.calendarEventVersion.summary,
            trustedHtml: calendarEventEmailHtml,
            // Force popout so that the composer does not
            // get hidden behind the fullscreen calendar
            shouldForcePopout: true,
            recipients,
          }),
        );

        trackInterfaceEvent(InterfaceEventsEnum.CLICK_EMAIL_CALENDAR_PARTICIPANTS);
      },
    };
  },
);

export const CalendarEventViewerHeaderButtonsContainer = connect(
  null,
  mapDispatchToProps,
)(CalendarEventViewerHeaderButtons);
